import React, { useCallback, useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Info, RotateCcw, ChevronLeft, ChevronRight, X, Cpu, ChevronDown, Lock, Unlock, Plus, EyeOff } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { useTheme } from '../../hooks/useTheme';
// Types
export interface ModelRun {
  modelId: string;
  timestamp: number;
}
export interface ModelConfig {
  id: string;
  name: string;
  color: string;
  badge?: string;
  badgeColor?: 'pink' | 'blue' | 'default';
  availability: number;
}
export const MODELS: ModelConfig[] = [
// AI Models
{
  id: 'ept-2',
  name: 'EPT-2',
  color: '#EC4899',
  availability: 1.0
}, {
  id: 'ept-2-early',
  name: 'EPT-2 Early',
  color: '#d946ef',
  badge: 'Early',
  badgeColor: 'pink',
  availability: 0.9
}, {
  id: 'ept-2-hrrr',
  name: 'EPT-2 HRRR',
  color: '#10b981',
  badge: 'Hourly',
  badgeColor: 'pink',
  availability: 0.85
}, {
  id: 'ept-2-rr',
  name: 'EPT-2 RR',
  color: '#f43f5e',
  badge: 'RR',
  badgeColor: 'pink',
  availability: 0.8
}, {
  id: 'ept-2e',
  name: 'EPT-2e',
  color: '#8b5cf6',
  availability: 0.8
}, {
  id: 'ec-aifs',
  name: 'EC AIFS',
  color: '#14b8a6',
  badge: 'AI',
  badgeColor: 'blue',
  availability: 0.9
}, {
  id: 'ms-aurora',
  name: 'Microsoft Aurora',
  color: '#0ea5e9',
  badge: 'AI',
  badgeColor: 'blue',
  availability: 0.75
}, {
  id: 'ec-ens',
  name: 'EC ENS',
  color: '#a78bfa',
  availability: 0.85
},
// NWP Models
{
  id: 'ec-ifs',
  name: 'EC-IFS',
  color: '#06b6d4',
  availability: 1.0
}, {
  id: 'gfs',
  name: 'GFS',
  color: '#F97316',
  availability: 1.0
}, {
  id: 'icon-global',
  name: 'ICON Global',
  color: '#eab308',
  availability: 0.9
}, {
  id: 'icon-eu',
  name: 'ICON-EU',
  color: '#84cc16',
  badge: 'EU',
  badgeColor: 'blue',
  availability: 0.85
},
// Ensemble / Live
{
  id: 'ec-ens-mean',
  name: 'EC Ens Mean',
  color: '#c084fc',
  badge: 'Live',
  badgeColor: 'default',
  availability: 0.7
}, {
  id: 'gfs-ens-mean',
  name: 'GFS Ens Mean',
  color: '#fb923c',
  badge: 'Live',
  badgeColor: 'default',
  availability: 0.7
}];
const RUN_HOURS = [0, 6, 12, 18];
// HRRR sub-hour offsets within each 6h block (6 bars per block, 1 per hour)
const HRRR_SUB_OFFSETS = [0, 1, 2, 3, 4, 5];
const isHourlyModel = (modelId: string) => modelId === 'ept-2-hrrr';
// Compute the 2 most recent 6-hourly runs for a given model
function getRecentRuns(modelId: string, count: number): ModelRun[] {
  const now = new Date();
  const runs: ModelRun[] = [];
  if (isHourlyModel(modelId)) {
    for (let i = 0; i < count; i++) {
      const d = new Date(now);
      d.setUTCHours(d.getUTCHours() - i, 0, 0, 0);
      runs.push({
        modelId,
        timestamp: d.getTime()
      });
    }
  } else {
    const baseHour = Math.floor(now.getUTCHours() / 6) * 6;
    for (let i = 0; i < count; i++) {
      const d = new Date(now);
      d.setUTCHours(baseHour - i * 6, 0, 0, 0);
      runs.push({
        modelId,
        timestamp: d.getTime()
      });
    }
  }
  return runs;
}
// Default: Latest run only from EPT-2, EC-IFS, and EC ENS
const DEFAULT_MODELS = ['ept-2', 'ec-ifs', 'ec-ens'];
export function getDefaultRuns(): ModelRun[] {
  return DEFAULT_MODELS.flatMap(id => getRecentRuns(id, 1));
}
// Models not in the default set start hidden
export function getDefaultHiddenModels(): Set<string> {
  return new Set(MODELS.filter(m => !DEFAULT_MODELS.includes(m.id)).map(m => m.id));
}
export interface ModelOverride {
  modelNames: string[];
  runs: {
    modelId: string;
    modelName: string;
    timestamp: number;
  }[];
}
interface ModelRunSelectorProps {
  onOverrideChange?: (override: ModelOverride | null) => void;
  inline?: boolean;
  compact?: boolean;
  hideOverride?: boolean;
  // Controlled state props for syncing multiple instances
  selectedRuns?: ModelRun[];
  onSelectedRunsChange?: (runs: ModelRun[]) => void;
  hiddenModels?: Set<string>;
  onHiddenModelsChange?: (models: Set<string>) => void;
}
export function ModelRunSelector({
  onOverrideChange,
  inline = false,
  compact = false,
  hideOverride = false,
  selectedRuns: controlledRuns,
  onSelectedRunsChange,
  hiddenModels: controlledHidden,
  onHiddenModelsChange
}: ModelRunSelectorProps) {
  const {
    theme
  } = useTheme();
  const isLight = theme === 'light';
  const [open, setOpen] = useState(false);
  const [overrideActive, setOverrideActive] = useState(false);
  const [internalRuns, setInternalRuns] = useState<ModelRun[]>(getDefaultRuns);
  const [viewStartDate, setViewStartDate] = useState(() => {
    // Start so tomorrow (today+1) is the rightmost visible day
    const tomorrow = new Date();
    tomorrow.setUTCHours(0, 0, 0, 0);
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    const start = new Date(tomorrow);
    start.setUTCDate(start.getUTCDate() - 6); // dayCount-1 = 6, so tomorrow is last column
    return start;
  });
  const [internalHidden, setInternalHidden] = useState<Set<string>>(getDefaultHiddenModels);
  // Use controlled or internal state
  const selectedRuns = controlledRuns ?? internalRuns;
  const setSelectedRuns = onSelectedRunsChange ? (updater: ModelRun[] | ((prev: ModelRun[]) => ModelRun[])) => {
    if (typeof updater === 'function') {
      onSelectedRunsChange(updater(selectedRuns));
    } else {
      onSelectedRunsChange(updater);
    }
  } : setInternalRuns;
  const hiddenModels = controlledHidden ?? internalHidden;
  const setHiddenModels = onHiddenModelsChange ? (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    if (typeof updater === 'function') {
      onHiddenModelsChange(updater(hiddenModels));
    } else {
      onHiddenModelsChange(updater);
    }
  } : setInternalHidden;
  const [addModelOpen, setAddModelOpen] = useState(false);
  const addModelRef = useRef<HTMLDivElement>(null);
  const addModelDropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const rowsScrollRef = useRef<HTMLDivElement>(null);
  const isSyncing = useRef(false);
  const syncScroll = useCallback((source: 'header' | 'rows') => {
    if (isSyncing.current) return;
    isSyncing.current = true;
    const from = source === 'header' ? headerScrollRef.current : rowsScrollRef.current;
    const to = source === 'header' ? rowsScrollRef.current : headerScrollRef.current;
    if (from && to) {
      to.scrollLeft = from.scrollLeft;
    }
    requestAnimationFrame(() => {
      isSyncing.current = false;
    });
  }, []);
  const visibleModels = MODELS.filter(m => !hiddenModels.has(m.id));
  const hiddenModelsList = MODELS.filter(m => hiddenModels.has(m.id));
  const hideModel = (modelId: string) => {
    setHiddenModels(prev => new Set([...prev, modelId]));
    // Also clear runs for this model
    setSelectedRuns(prev => prev.filter(r => r.modelId !== modelId));
  };
  const showModel = (modelId: string) => {
    setHiddenModels(prev => {
      const next = new Set(prev);
      next.delete(modelId);
      return next;
    });
    // Automatically select the latest run for the newly added model
    selectLatest(modelId);
  };
  // Close add-model dropdown on outside click
  useEffect(() => {
    if (!addModelOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      // Check both the trigger area and the portaled dropdown
      if (addModelRef.current && !addModelRef.current.contains(target) && (!addModelDropdownRef.current || !addModelDropdownRef.current.contains(target))) {
        setAddModelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [addModelOpen]);
  // Generate days — up to dayCount visible days, but never past today
  const dayCount = compact ? 7 : 7;
  const todayMidnight = new Date();
  todayMidnight.setUTCHours(0, 0, 0, 0);
  const timelineDays = Array.from({
    length: dayCount
  }, (_, i) => {
    const d = new Date(viewStartDate);
    d.setUTCDate(d.getUTCDate() + i);
    return d;
  }).filter(d => d.getTime() <= todayMidnight.getTime());
  // Click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current && !panelRef.current.contains(target) && triggerRef.current && !triggerRef.current.contains(target) && (!addModelDropdownRef.current || !addModelDropdownRef.current.contains(target))) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  const toggleRun = (modelId: string, timestamp: number) => {
    setSelectedRuns(prev => {
      const exists = prev.find(r => r.modelId === modelId && r.timestamp === timestamp);
      if (exists) return prev.filter(r => r !== exists);
      return [...prev, {
        modelId,
        timestamp
      }];
    });
  };
  const selectLatest = (modelId: string) => {
    const now = new Date();
    let timestamp: number;
    if (isHourlyModel(modelId)) {
      // For HRRR: round down to the current hour
      now.setUTCMinutes(0, 0, 0);
      timestamp = now.getTime();
    } else {
      // For standard models: round down to nearest 6h block
      const hour = Math.floor(now.getUTCHours() / 6) * 6;
      now.setUTCHours(hour, 0, 0, 0);
      timestamp = now.getTime();
    }
    setSelectedRuns(prev => {
      const withoutModel = prev.filter(r => r.modelId !== modelId);
      return [...withoutModel, {
        modelId,
        timestamp
      }];
    });
    jumpToToday();
  };
  const clearModel = (modelId: string) => {
    setSelectedRuns(prev => prev.filter(r => r.modelId !== modelId));
  };
  const resetAll = () => {
    setSelectedRuns(getDefaultRuns());
    setHiddenModels(getDefaultHiddenModels());
    if (overrideActive) {
      setOverrideActive(false);
      onOverrideChange?.(null);
    }
  };
  // Build override payload from current selections
  const buildOverride = (runs: ModelRun[]): ModelOverride => {
    const uniqueIds = [...new Set(runs.map(r => r.modelId))];
    const names = uniqueIds.map(id => MODELS.find(m => m.id === id)).filter(Boolean).map(m => m!.name);
    return {
      modelNames: names,
      runs: runs.map(r => {
        const model = MODELS.find(m => m.id === r.modelId);
        return {
          modelId: r.modelId,
          modelName: model?.name || r.modelId,
          timestamp: r.timestamp
        };
      })
    };
  };
  const toggleOverride = () => {
    const next = !overrideActive;
    setOverrideActive(next);
    if (next && selectedRuns.length > 0) {
      onOverrideChange?.(buildOverride(selectedRuns));
    } else {
      onOverrideChange?.(null);
    }
  };
  // Notify parent when runs change while override is active (or always in hideOverride/page mode)
  useEffect(() => {
    if (hideOverride) {
      // In page mode, always report current selections
      if (selectedRuns.length > 0) {
        onOverrideChange?.(buildOverride(selectedRuns));
      } else {
        onOverrideChange?.(null);
      }
    } else if (overrideActive && selectedRuns.length > 0) {
      onOverrideChange?.(buildOverride(selectedRuns));
    } else if (overrideActive && selectedRuns.length === 0) {
      setOverrideActive(false);
      onOverrideChange?.(null);
    }
  }, [selectedRuns, overrideActive, hideOverride]);
  // Max viewStartDate: today is the last visible day (rightmost)
  const getMaxStartDate = () => {
    const maxRight = new Date();
    maxRight.setUTCHours(0, 0, 0, 0);
    // today is the rightmost day
    const max = new Date(maxRight);
    max.setUTCDate(max.getUTCDate() - (dayCount - 1));
    return max;
  };
  // Scroll-based navigation with smooth scrolling
  const scrollTimeline = (days: number) => {
    const d = new Date(viewStartDate);
    d.setUTCDate(d.getUTCDate() + days);
    const maxStart = getMaxStartDate();
    if (d.getTime() > maxStart.getTime()) {
      setViewStartDate(maxStart);
    } else {
      setViewStartDate(d);
    }
  };
  const jumpToToday = () => {
    const maxStart = getMaxStartDate();
    setViewStartDate(maxStart);
  };
  // Scroll to end (rightmost / most recent) on mount and when viewStartDate changes
  useEffect(() => {
    const scrollToEnd = () => {
      if (headerScrollRef.current) {
        headerScrollRef.current.scrollTo({
          left: headerScrollRef.current.scrollWidth,
          behavior: 'smooth'
        });
      }
      if (rowsScrollRef.current) {
        rowsScrollRef.current.scrollTo({
          left: rowsScrollRef.current.scrollWidth,
          behavior: 'smooth'
        });
      }
    };
    requestAnimationFrame(() => {
      scrollToEnd();
    });
  }, [viewStartDate]);
  const formatRunTime = (timestamp: number) => {
    const d = new Date(timestamp);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC'
    });
  };
  const getMonthLabel = () => {
    const midIndex = Math.min(3, timelineDays.length - 1);
    return timelineDays[midIndex].toLocaleString('en-US', {
      month: 'long',
      timeZone: 'UTC'
    });
  };
  const isToday = (date: Date) => {
    const today = new Date();
    return date.getUTCDate() === today.getUTCDate() && date.getUTCMonth() === today.getUTCMonth() && date.getUTCFullYear() === today.getUTCFullYear();
  };
  // Summary for the trigger button
  const uniqueModels = [...new Set(selectedRuns.map(r => r.modelId))];
  const modelNames = uniqueModels.map(id => MODELS.find(m => m.id === id)?.name).filter(Boolean);
  const summaryText = selectedRuns.length === 0 ? 'No runs' : `${selectedRuns.length} run${selectedRuns.length !== 1 ? 's' : ''}`;
  const getPanelPosition = () => {
    if (!triggerRef.current) return {
      top: 0,
      right: 0
    };
    const rect = triggerRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right
    };
  };
  const dropdownStyle: React.CSSProperties = {
    background: isLight ? 'rgba(255, 255, 255, 0.97)' : 'rgba(11, 17, 32, 0.97)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  // Shared panel content (used by both inline and dropdown modes)
  const panelContent = <>
      {/* Header */}
      <div className={`relative flex flex-col sm:flex-row items-center justify-between gap-2 px-3 py-2.5 ${inline ? '' : `border-b ${isLight ? 'border-black/[0.06]' : 'border-white/[0.06]'}`}`} data-id="element-1861">
        <div className="flex items-center gap-2 justify-center sm:justify-start w-full sm:w-auto" data-id="element-1862">
          <h3 className={`text-xs font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1863">
            Select model
          </h3>
        </div>
        <div className="flex items-center gap-2" data-id="element-1864">
          {!inline && <button onClick={() => setOpen(false)} className={`p-1.5 rounded-md transition-colors ${isLight ? 'text-slate-400 hover:text-slate-900 hover:bg-black/[0.06]' : 'text-slate-500 hover:text-white hover:bg-white/[0.06]'}`} data-id="element-1865">
              <X size={16} data-id="element-1866" />
            </button>}
        </div>
      </div>

      {/* Selected Runs Pills — hidden in compact and inline modes */}
      {!compact && !inline && <div className={`relative px-5 py-3 border-b ${isLight ? 'border-black/[0.06]' : 'border-white/[0.06]'}`} data-id="element-1867">
          <div className={`text-[11px] uppercase tracking-wider font-medium mb-2 ${isLight ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-1868">
            Selected runs
          </div>
          <div className="flex flex-wrap gap-1.5" data-id="element-1869">
            {selectedRuns.length === 0 && <span className={`text-xs italic ${isLight ? 'text-slate-400' : 'text-slate-600'}`} data-id="element-1870">
                No runs selected — use the grid below
              </span>}
            {MODELS.map(model => {
          const runs = selectedRuns.filter(r => r.modelId === model.id).sort((a, b) => b.timestamp - a.timestamp);
          if (runs.length === 0) return null;
          return <div key={model.id} className="flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md text-[11px] font-medium border transition-colors" style={{
            backgroundColor: `${model.color}15`,
            borderColor: `${model.color}30`
          }} data-id="element-1871">
                  <div className="flex items-center gap-1.5" data-id="element-1872">
                    <div className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: model.color
              }} data-id="element-1873" />
                    <span className={isLight ? 'text-slate-700' : 'text-slate-200'} data-id="element-1874">
                      {model.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 ml-1" data-id="element-1875">
                    {runs.map(run => <div key={run.timestamp} className={`flex items-center gap-0.5 pl-1.5 pr-0.5 py-0.5 rounded-[4px] ${isLight ? 'bg-white/60 shadow-sm' : 'bg-black/20'}`} data-id="element-1876">
                        <span className={`font-mono text-[10px] ${isLight ? 'text-slate-600' : 'text-slate-300'}`} data-id="element-1877">
                          {formatRunTime(run.timestamp)}
                        </span>
                        <button onClick={() => toggleRun(model.id, run.timestamp)} className={`p-0.5 rounded transition-colors ${isLight ? 'hover:bg-black/[0.1] text-slate-400 hover:text-slate-900' : 'hover:bg-white/[0.1] text-slate-500 hover:text-white'}`} data-id="element-1878">
                          <X size={10} data-id="element-1879" />
                        </button>
                      </div>)}
                  </div>
                </div>;
        })}
          </div>
        </div>}

      {/* Timeline Grid - 3 true columns: left fixed, middle scrollable, right fixed */}
      <div className="relative flex flex-1 overflow-hidden" data-id="element-1880">
        {/* LEFT COLUMN — arrow header + model name cells */}
        <div className={`${compact ? 'w-20' : 'w-20'} flex-shrink-0 border-r flex flex-col ${isLight ? 'border-black/[0.06]' : 'border-white/[0.06]'}`} data-id="element-1881">
          {/* Arrow header */}
          <button onClick={() => scrollTimeline(-1)} className={`${compact ? 'h-[42px]' : 'h-[48px]'} flex-shrink-0 border-b flex items-center justify-center cursor-pointer transition-colors ${isLight ? 'border-black/[0.06] bg-black/[0.02] hover:bg-black/[0.05] text-slate-400 hover:text-slate-900' : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.06] text-slate-500 hover:text-white'}`} data-id="element-1882">
            <ChevronLeft size={16} data-id="element-1883" />
          </button>
          {/* Model name cells */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden" data-id="element-1884">
            {visibleModels.map(model => <div key={model.id} className={`${compact ? 'h-[34px] px-1.5' : 'h-[38px] px-2'} border-b ${isLight ? 'border-black/[0.04]' : 'border-white/[0.04]'} flex items-center gap-1.5`} data-id="element-1885">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
              backgroundColor: model.color
            }} data-id="element-1886" />
                <span className={`text-xs font-bold truncate ${isLight ? 'text-slate-700' : 'text-slate-200'}`} data-id="element-1887">
                  {model.name}
                </span>
              </div>)}
          </div>
        </div>

        {/* MIDDLE COLUMN — date headers + checkbox rows, scrolls horizontally */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0" data-id="element-1888">
          {/* Date headers */}
          <div className="overflow-x-auto overflow-y-hidden flex-shrink-0 scrollbar-hide" ref={headerScrollRef} onScroll={() => syncScroll('header')} data-id="element-1889">
            <div className={`flex min-w-max ${compact ? 'h-[42px]' : 'h-[48px]'} border-b ${isLight ? 'border-black/[0.06]' : 'border-white/[0.06]'}`} data-id="element-1890">
              {timelineDays.map(day => <div key={day.toISOString()} className={`${compact ? 'w-20' : 'w-24'} flex-shrink-0 border-r ${isLight ? 'border-black/[0.06]' : 'border-white/[0.06]'} flex flex-col items-center justify-end pb-1 ${isToday(day) ? 'bg-[#EC4899]/5' : ''}`} data-id="element-1891">
                  <div className={`text-sm font-bold mb-0.5 w-full text-center ${isToday(day) ? 'text-[#EC4899]' : isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1892">
                    {day.getUTCDate()}
                  </div>
                  <div className="grid grid-cols-4 w-full px-1" data-id="element-1893">
                    {RUN_HOURS.map(h => <div key={h} className={`text-[10px] font-mono text-center leading-tight ${isLight ? 'text-slate-400' : 'text-slate-600'}`} data-id="element-1894">
                        {h.toString().padStart(2, '0')}
                      </div>)}
                  </div>
                </div>)}
              {/* Right padding so the last day cell isn't hidden under the arrow column */}
              <div className="w-4 flex-shrink-0" data-id="element-1895" />
            </div>
          </div>
          {/* Checkbox rows */}
          <div className="flex-1 overflow-x-auto overflow-y-auto scrollbar-hide" ref={rowsScrollRef} onScroll={() => syncScroll('rows')} data-id="element-1896">
            {visibleModels.map(model => <div key={model.id} className={`flex min-w-max ${compact ? 'h-[34px]' : 'h-[38px]'} border-b ${isLight ? 'border-black/[0.04]' : 'border-white/[0.04]'}`} data-id="element-1897">
                {timelineDays.map(day => <div key={day.toISOString()} className={`${compact ? 'w-20' : 'w-24'} flex-shrink-0 border-r ${isLight ? 'border-black/[0.06]' : 'border-white/[0.06]'} flex items-center ${isToday(day) ? 'bg-[#EC4899]/5' : ''} ${isHourlyModel(model.id) ? 'gap-0 px-1' : 'px-1.5'}`} data-id="element-1898">
                    {isHourlyModel(model.id) ? RUN_HOURS.map(hour => <div key={hour} className="flex-1 flex items-center gap-[1px] px-[1px]" data-id="element-1899">
                          {HRRR_SUB_OFFSETS.map((offset, oi) => {
                  const runTime = new Date(day);
                  const subHour = hour + offset;
                  runTime.setUTCHours(Math.floor(subHour), subHour % 1 * 60, 0, 0);
                  const timestamp = runTime.getTime();
                  const isSelected = selectedRuns.some(r => r.modelId === model.id && r.timestamp === timestamp);
                  const isFuture = timestamp > Date.now();
                  return <button key={oi} onClick={() => !isFuture && toggleRun(model.id, timestamp)} disabled={isFuture} className={`flex-1 h-[30px] rounded-[3px] transition-all ${isFuture ? isLight ? 'bg-slate-200/30 cursor-not-allowed' : 'bg-slate-800/20 cursor-not-allowed' : isSelected ? '' : isLight ? 'bg-slate-200/50 hover:bg-slate-300/70 cursor-pointer' : 'bg-slate-700/30 hover:bg-slate-600/50 cursor-pointer'}`} style={{
                    backgroundColor: isSelected ? model.color : undefined
                  }} title={`${model.name} · ${formatRunTime(timestamp)}`} data-id="element-1900" />;
                })}
                        </div>) : <div className="grid grid-cols-4 w-full gap-0" data-id="element-1901">
                        {RUN_HOURS.map(hour => {
                  const runTime = new Date(day);
                  runTime.setUTCHours(hour, 0, 0, 0);
                  const timestamp = runTime.getTime();
                  const isSelected = selectedRuns.some(r => r.modelId === model.id && r.timestamp === timestamp);
                  const isFuture = timestamp > Date.now();
                  return <div key={hour} className="flex items-center justify-center" data-id="element-1902">
                              <button onClick={() => !isFuture && toggleRun(model.id, timestamp)} disabled={isFuture} className={`${compact ? 'w-[13px] h-[13px]' : 'w-[17px] h-[17px]'} rounded-[3px] border transition-all flex items-center justify-center ${isSelected ? 'border-transparent' : isLight ? 'border-slate-300/70 bg-slate-100/60 hover:border-slate-400' : 'border-slate-700/60 bg-slate-800/30 hover:border-slate-500'} ${isFuture ? 'opacity-15 cursor-not-allowed' : 'cursor-pointer'}`} style={{
                      backgroundColor: isSelected ? model.color : undefined
                    }} title={`${model.name} · ${formatRunTime(timestamp)}`} data-id="element-1903">
                                {isSelected && <svg width={compact ? 7 : 10} height={compact ? 7 : 10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="text-white" data-id="element-1904">
                                    <polyline points="20 6 9 17 4 12" data-id="element-1905" />
                                  </svg>}
                              </button>
                            </div>;
                })}
                      </div>}
                  </div>)}
                {/* Right padding so the last day cell isn't hidden under the arrow column */}
                <div className="w-4 flex-shrink-0" data-id="element-1906" />
              </div>)}
          </div>
        </div>

        {/* RIGHT COLUMN — arrow header + Latest button cells */}
        <div className={`${compact ? 'w-16' : 'w-18'} flex-shrink-0 border-l flex flex-col ${isLight ? 'border-black/[0.06] shadow-[-8px_0_16px_-4px_rgba(0,0,0,0.08)]' : 'border-white/[0.06] shadow-[-8px_0_16px_-4px_rgba(0,0,0,0.5)]'}`} data-id="element-1907">
          {/* Arrow header */}
          <button onClick={() => scrollTimeline(1)} className={`${compact ? 'h-[42px]' : 'h-[48px]'} flex-shrink-0 border-b flex items-center justify-center cursor-pointer transition-colors ${isLight ? 'border-black/[0.06] bg-black/[0.02] hover:bg-black/[0.05] text-slate-400 hover:text-slate-900' : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.06] text-slate-500 hover:text-white'}`} data-id="element-1908">
            <ChevronRight size={16} data-id="element-1909" />
          </button>
          {/* Latest button cells */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden" data-id="element-1910">
            {visibleModels.map(model => <div key={model.id} className={`${compact ? 'h-[34px] px-1' : 'h-[38px] px-1.5'} border-b ${isLight ? 'border-black/[0.04]' : 'border-white/[0.04]'} flex items-center justify-center`} data-id="element-1911">
                <button onClick={() => selectLatest(model.id)} className={`px-2 py-1 rounded text-[11px] font-medium transition-colors border ${isLight ? 'bg-black/[0.04] hover:bg-black/[0.08] text-slate-600 hover:text-slate-900 border-black/[0.06]' : 'bg-white/[0.06] hover:bg-white/[0.1] text-slate-300 hover:text-white border-white/[0.04]'}`} data-id="element-1912">
                  Latest
                </button>
              </div>)}
          </div>
        </div>
      </div>

      {/* Add Model — bottom section */}
      <div className={`px-3 py-2.5 border-t ${isLight ? 'border-black/[0.06]' : 'border-white/[0.06]'}`} data-id="element-1913">
        <div className="relative" ref={addModelRef} data-id="element-1914">
          <button onClick={() => setAddModelOpen(!addModelOpen)} className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all border`} style={{
          backgroundColor: addModelOpen ? isLight ? 'rgba(236,72,153,0.08)' : 'rgba(236,72,153,0.12)' : isLight ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.04)',
          borderColor: isLight ? 'rgba(148,163,184,0.25)' : 'rgba(255,255,255,0.08)',
          backdropFilter: isLight ? 'blur(20px) saturate(180%)' : undefined,
          WebkitBackdropFilter: isLight ? 'blur(20px) saturate(180%)' : undefined,
          color: addModelOpen ? '#EC4899' : isLight ? '#475569' : '#94a3b8'
        }} data-id="element-1915">
            <Plus size={12} data-id="element-1916" />
            Add Model ({visibleModels.length}/{MODELS.length})
          </button>

          {addModelOpen && ReactDOM.createPortal(<div ref={addModelDropdownRef} className={`fixed rounded-lg border shadow-2xl min-w-[200px] flex flex-col ${isLight ? 'border-black/[0.08] shadow-black/10' : 'border-white/[0.08] shadow-black/60'}`} style={{
          zIndex: 10001,
          background: isLight ? 'rgba(255, 255, 255, 0.97)' : 'rgba(11, 17, 32, 0.97)',
          backdropFilter: 'blur(20px)',
          maxHeight: 'min(400px, 50vh)',
          overflowY: 'hidden',
          ...(addModelRef.current ? (() => {
            const rect = addModelRef.current!.getBoundingClientRect();
            const dropdownHeight = 400;
            const spaceAbove = rect.top - 8;
            const spaceBelow = window.innerHeight - rect.bottom - 8;
            const openAbove = spaceAbove >= Math.min(dropdownHeight, spaceBelow + 100);
            if (openAbove) {
              return {
                left: rect.left,
                bottom: window.innerHeight - rect.top + 6,
                maxHeight: Math.min(dropdownHeight, spaceAbove)
              };
            }
            return {
              left: rect.left,
              top: rect.bottom + 6,
              maxHeight: Math.min(dropdownHeight, spaceBelow)
            };
          })() : {})
        }} data-id="element-1917">
                {/* Select All */}
                <button onClick={() => {
            if (hiddenModels.size === 0) {
              setHiddenModels(new Set(MODELS.slice(1).map(m => m.id)));
            } else {
              setHiddenModels(new Set());
            }
          }} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors border-b ${isLight ? 'hover:bg-black/[0.04] border-black/[0.06]' : 'hover:bg-white/[0.06] border-white/[0.06]'}`} data-id="element-1918">
                  <div className={`w-[16px] h-[16px] rounded-[3px] border flex items-center justify-center transition-all flex-shrink-0 ${hiddenModels.size === 0 ? 'bg-[#EC4899] border-transparent' : isLight ? 'border-slate-300' : 'border-slate-600'}`} data-id="element-1919">
                    {hiddenModels.size === 0 && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="text-white" data-id="element-1920">
                        <polyline points="20 6 9 17 4 12" data-id="element-1921" />
                      </svg>}
                  </div>
                  <span className={`text-xs font-medium ${isLight ? 'text-slate-700' : 'text-slate-200'}`} data-id="element-1922">
                    All Models
                  </span>
                </button>

                <div className="py-1 overflow-y-auto flex-1 min-h-0" data-id="element-1923">
                  {MODELS.map(model => {
              const isVisible = !hiddenModels.has(model.id);
              return <button key={model.id} onClick={() => {
                if (isVisible) {
                  hideModel(model.id);
                } else {
                  showModel(model.id);
                }
              }} className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors ${isLight ? 'hover:bg-black/[0.06]' : 'hover:bg-white/[0.08]'}`} data-id="element-1924">
                        <div className={`w-[16px] h-[16px] rounded-[3px] border flex items-center justify-center transition-all flex-shrink-0`} style={{
                  backgroundColor: isVisible ? model.color : 'transparent',
                  borderColor: isVisible ? 'transparent' : isLight ? 'rgba(148,163,184,0.7)' : 'rgba(148,163,184,0.35)'
                }} data-id="element-1925">
                          {isVisible && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="text-white" data-id="element-1926">
                              <polyline points="20 6 9 17 4 12" data-id="element-1927" />
                            </svg>}
                        </div>
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                  backgroundColor: model.color,
                  opacity: isVisible ? 1 : 0.65
                }} data-id="element-1928" />
                        <span className="text-xs font-medium flex-1" style={{
                  color: isVisible ? isLight ? '#334155' : '#e2e8f0' : isLight ? '#64748b' : '#94a3b8'
                }} data-id="element-1929">
                          {model.name}
                        </span>
                        {model.badge && <Badge variant={model.badgeColor || 'default'} className="w-fit px-1 py-0 text-[7px] h-3" data-id="element-1930">
                            {model.badge}
                          </Badge>}
                      </button>;
            })}
                </div>
              </div>, document.body)}
        </div>
      </div>
    </>;
  // Inline mode: render panel directly in the DOM (transparent background to blend with card)
  if (inline) {
    return <div className={`rounded-xl border overflow-hidden flex flex-col ${isLight ? '' : 'border-white/[0.08]'}`} style={{
      borderColor: isLight ? 'rgba(255, 255, 255, 0.80)' : undefined,
      backgroundColor: isLight ? 'rgba(255, 255, 255, 0.65)' : 'var(--bg-surface)',
      backdropFilter: isLight ? 'blur(20px) saturate(180%)' : undefined,
      WebkitBackdropFilter: isLight ? 'blur(20px) saturate(180%)' : undefined,
      boxShadow: isLight ? '0 2px 8px rgba(148, 163, 184, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.95) inset' : undefined
    }} data-id="element-1931">
        {panelContent}
      </div>;
  }
  // Dropdown mode: trigger button + portal
  return <>
      <button ref={triggerRef} onClick={() => setOpen(!open)} className={`jua-toolbar-btn flex items-center gap-2 h-8 px-3 rounded-lg text-xs font-medium border transition-all ${overrideActive ? 'bg-amber-500/15 border-amber-500/40 text-amber-400' : open ? 'bg-[#EC4899]/10 border-[#EC4899]/30 text-[#EC4899]' : 'bg-white/[0.04] border-white/[0.08] text-slate-300 hover:bg-white/[0.08] hover:text-white'}`} data-id="element-1932">
        {overrideActive ? <Lock size={12} className="text-amber-400" data-id="element-1933" /> : <Cpu size={12} className={open ? 'text-[#EC4899]' : 'text-slate-500'} data-id="element-1934" />}
        <span className="font-bold" data-id="element-1935">
          {overrideActive ? 'Override' : 'Model'}
        </span>
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''} ${overrideActive ? 'text-amber-500' : open ? 'text-[#EC4899]' : 'text-slate-500'}`} data-id="element-1936" />
      </button>

      {open && ReactDOM.createPortal(<>
            {/* Backdrop overlay */}
            <div className="fixed inset-0 z-[9998]" style={{
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(2px)'
      }} onClick={() => setOpen(false)} data-id="element-1937" />
            <div ref={panelRef} className="jua-model-run-panel fixed rounded-xl shadow-2xl shadow-black/60 border border-white/[0.08] overflow-hidden z-[9999] flex flex-col" style={{
        ...dropdownStyle,
        top: getPanelPosition().top,
        left: '50%',
        transform: 'translateX(-50%)',
        width: Math.min(820, window.innerWidth - 32),
        maxHeight: 620
      }} data-id="element-1938">
              <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 50%, rgba(255,255,255,0.01) 100%)'
        }} data-id="element-1939" />
              {panelContent}
            </div>
          </>, document.body)}
    </>;
}